import { Variant } from '@amplitude/experiment-js-client';
import { useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { useGetLoggedAgent } from '../../resources/agent/hooks';
import { useAuth } from '../providers/AuthProvider';
import { useGetDossierData } from '../../resources/dossier/queries';

import { ampli, Ampli, DefaultPropertyGroupProperties, TripProperties } from './__AUTO_GENERATED__';
import { AmplitudeContext } from './amplitude-provider';

type AmplitudeCallback = (ampli: Ampli, defaultProperties: DefaultPropertyGroupProperties) => void;

export function useAmplitude() {
    return useContext(AmplitudeContext);
}

export const useTrackEventAmplitude = () => {
    const { isAmplitudeLoaded } = useAmplitude();

    const trackEvent = useCallback(
        (callback: AmplitudeCallback) => {
            if (isAmplitudeLoaded) {
                callback(ampli, getDefaultTrackingProperties());
            }
        },
        [isAmplitudeLoaded],
    );

    return {
        trackEvent,
        isAmplitudeLoaded,
    };
};

export const useTrackPageViewAmplitude = (callback: AmplitudeCallback) => {
    const { isAmplitudeLoaded } = useAmplitude();
    const callbackRef = useRef(callback);

    useEffect(() => {
        if (isAmplitudeLoaded) {
            callbackRef.current(ampli, getDefaultTrackingProperties());
        }
    }, [isAmplitudeLoaded]);
};

export const useTrackPageViewAmplitudeOnce = (
    trackCallback: AmplitudeCallback,
    options: {
        dependencies: unknown[];
        isReady: boolean;
    } = { dependencies: [], isReady: true },
) => {
    const { isAmplitudeLoaded } = useAmplitude();
    const hasTrackedRef = useRef(false);

    useEffect(() => {
        if (isAmplitudeLoaded && !hasTrackedRef.current && options.isReady) {
            trackCallback(ampli, getDefaultTrackingProperties());
            hasTrackedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAmplitudeLoaded, ...options.dependencies]);
};

const getDefaultTrackingProperties = (): DefaultPropertyGroupProperties => {
    return {
        ets_source: 'request_manager',
    };
};

type AssignationData = Record<string, string>;
export const useExperimentVariation = (
    experimentName: string,
    assignationData?: AssignationData,
) => {
    const { isAmplitudeLoaded, experiment } = useAmplitude();
    const [variant, setVariant] = useState<Variant>();
    const refAssignationData = useRef(assignationData);

    useEffect(() => {
        if (isAmplitudeLoaded && experiment) {
            void experiment
                .fetch({
                    // Only used for experiment assignations
                    user_properties: {
                        ...experiment.getUser().user_properties,
                        ...refAssignationData.current,
                    },
                })

                .then(() => {
                    setVariant(experiment.variant(experimentName));
                });
        }
    }, [experiment, experimentName, isAmplitudeLoaded]);

    return variant;
};

export const useGetTrackedTripProperties = (dossierId: string) => {
    const agent = useGetLoggedAgent();
    const { dossier } = useGetDossierData(dossierId) || {};
    const { userId: loggedAgentId } = useAuth();

    // Memoize the properties object to prevent unnecessary re-renders
    return useMemo(() => {
        // Those fallbacks are not ideal, but the tracking types are auto-generated and not flexible, so we need to use them for now, even though in practice they should never be used
        return {
            agency_id: agent?.agencyId.toString() ?? '',
            is_agent_assigned: loggedAgentId?.toString() === dossier?.agentId,
            destination_slug: dossier?.project?.destinationLabel ?? '',
            market_name: dossier?.project?.marketCode ?? '',
            request_id: parseInt(dossier?.id ?? ''),
            request_rating: dossier?.rating ?? 0,
            request_RM_step: dossier?.step ?? 'new_request',
            request_status: dossier?.task?.definition ?? '',
            requester_id: dossier?.project?.requesterId?.toString() ?? '',
        } as TripProperties;
    }, [
        agent?.agencyId,
        loggedAgentId,
        dossier?.agentId,
        dossier?.project?.destinationLabel,
        dossier?.project?.marketCode,
        dossier?.id,
        dossier?.rating,
        dossier?.step,
        dossier?.task?.definition,
        dossier?.project?.requesterId,
    ]);
};
